/**
 * Source component - Displays a source reference with favicon and hover popover
 * Compound component pattern: Source > SourceTrigger + SourceContent
 */

import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import { FileText } from 'lucide-react';

export interface SourceData {
  type: 'document' | 'web' | 'jurisprudence' | 'doctrine' | 'article';
  name: string;
  url?: string;
  description?: string;
  file_path?: string;
}

interface SourceContextValue {
  href?: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const SourceContext = createContext<SourceContextValue | null>(null);

function useSourceContext() {
  const context = useContext(SourceContext);
  if (!context) {
    throw new Error('Source components must be used within a Source provider');
  }
  return context;
}

function getDomain(url: string): string {
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return url;
  }
}

function getFaviconUrl(url: string): string {
  try {
    return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}&sz=32`;
  } catch {
    return '';
  }
}

// Root
interface SourceProps {
  href?: string;
  children: React.ReactNode;
  className?: string;
}

export function Source({ href, children, className = '' }: SourceProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null!);
  return (
    <SourceContext.Provider value={{ href, isOpen, setIsOpen, triggerRef }}>
      <div className={`relative inline-block ${className}`}>{children}</div>
    </SourceContext.Provider>
  );
}

// Trigger
interface SourceTriggerProps {
  showFavicon?: boolean;
  className?: string;
  children?: React.ReactNode;
}

export function SourceTrigger({ showFavicon = true, className = '', children }: SourceTriggerProps) {
  const { href, setIsOpen, triggerRef } = useSourceContext();
  const domain = href ? getDomain(href) : '';
  const faviconUrl = href ? getFaviconUrl(href) : '';
  const [faviconError, setFaviconError] = useState(false);

  return (
    <button
      ref={triggerRef}
      onClick={() => href && window.open(href, '_blank', 'noopener,noreferrer')}
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
      className={`
        inline-flex items-center gap-1 px-2 py-px
        bg-grey-100 hover:bg-grey-200
        border border-grey-200
        rounded-full
        text-xs text-grey-600
        transition-colors cursor-pointer
        leading-tight
        ${className}
      `}
    >
      {showFavicon && faviconUrl && !faviconError ? (
        <img src={faviconUrl} alt="" className="w-3 h-3 rounded-sm" onError={() => setFaviconError(true)} />
      ) : showFavicon ? (
        <FileText className="w-3 h-3 text-grey-400" />
      ) : null}
      {children || domain}
    </button>
  );
}

// Content (Popover)
interface SourceContentProps {
  title: string;
  description?: string;
  className?: string;
}

export function SourceContent({ title, description, className = '' }: SourceContentProps) {
  const { isOpen, triggerRef } = useSourceContext();
  const contentRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<'top' | 'bottom'>('bottom');

  useEffect(() => {
    if (isOpen && triggerRef.current && contentRef.current) {
      const triggerRect = triggerRef.current.getBoundingClientRect();
      const contentHeight = contentRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - triggerRect.bottom;
      if (spaceBelow < contentHeight + 10 && triggerRect.top > spaceBelow) {
        setPosition('top');
      } else {
        setPosition('bottom');
      }
    }
  }, [isOpen, triggerRef]);

  if (!isOpen) return null;

  return (
    <div
      ref={contentRef}
      className={`
        absolute z-50 w-64 p-3
        bg-white border border-grey-200 rounded-lg shadow-lg
        ${position === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'}
        left-0
        ${className}
      `}
    >
      <div className="text-sm font-medium text-grey-900 line-clamp-2">{title}</div>
      {description && (
        <div className="mt-1 text-xs text-grey-600 line-clamp-3">{description}</div>
      )}
    </div>
  );
}

// SourceList
interface SourceListProps {
  sources: SourceData[];
  className?: string;
}

export function SourceList({ sources, className = '' }: SourceListProps) {
  if (!sources || sources.length === 0) return null;
  return (
    <div className={`flex flex-wrap gap-1.5 ${className}`}>
      {sources.map((source, index) => (
        <Source key={index} href={source.url}>
          <SourceTrigger showFavicon={!!source.url}>
            {source.url ? getDomain(source.url) : source.name}
          </SourceTrigger>
          <SourceContent title={source.name} description={source.description} />
        </Source>
      ))}
    </div>
  );
}
